import React, { useState, useEffect, useCallback } from 'react';
import { View, TextInput, Animated, Text, StyleSheet, TouchableWithoutFeedback } from 'react-native';
import i18n from 'i18n-js';

import { scale } from '@components/frame';

import { isIOS, isPhone } from '@helpers';
import ColorWrapper from '@styles/colors';

import Svg from '@styles/icons/svg';
import { font } from '@styles/fonts';

const AnimatedInput = ({
  placeholder,
  errorText,
  error,
  errorAction,
  valid,

  disabled,
  secure,
  value,
  autoCompleteType,
  prefix,
  sufix,
  styleInput,
  styleLabel,
  styleError,
  styleContent,
  styleBodyContent,
  C,
  ...others
}) => {
  const [showInput, setShowInput] = useState(false);
  const [showError, setShowError] = useState(false);
  const [animatedIsFocused] = useState(new Animated.Value(value ? 0 : 1));
  const [isInputFocused, setInputFocus] = useState(false);
  const errorColor = C('Label / Light / Error');
  const inputFontSize = styleInput.fontSize || styles.input.fontSize;
  const labelFontSize = styleLabel.fontSize || styles.label.fontSize;
  const errorFontSize = styleError.fontSize || styles.error.fontSize;

  const animationLabelFontSize = useCallback(() => {
    const fontAdjust = {
      fontSize: animatedIsFocused.interpolate({
        inputRange: [0, 1],
        outputRange: [labelFontSize, inputFontSize],
      }),
      top: animatedIsFocused.interpolate({
        inputRange: [0, 1],
        outputRange: [scale(-8), scale(2)],
      }),
      color: animatedIsFocused.interpolate({
        inputRange: [0, 1],
        outputRange: [C('Label/Light/Primary'), C('Label/Light/Tertiary')],
      }),
      paddingTop: animatedIsFocused.interpolate({
        inputRange: [0, 1],
        outputRange: [0, scale(17)],
      }),
    };
    return fontAdjust;
  }, [animatedIsFocused, inputFontSize, labelFontSize, C.schemeName]);

  const startAnimation = useCallback(() => {
    Animated.timing(animatedIsFocused, {
      toValue: showInput ? 1 : 0,
      duration: 150,
    }).start(() => {
      if (!showInput) {
        setShowInput(true);
      }
    });
  }, [animatedIsFocused, showInput, C.schemeName]);

  useEffect(() => {
    setShowError(!valid);
    if (value) {
      setShowInput(true);
    }
  }, [valid, value, animationLabelFontSize, animatedIsFocused, startAnimation, showInput]);

  const onBlur = () => {
    setInputFocus(false);
    if (!value) {
      setShowInput(false);
      setShowError(false);
      startAnimation();
    }
  };

  const onFocus = () => {
    setInputFocus(true);
    if (!showInput) {
      startAnimation();
    }
  };

  const setContentHeight = () => {
    const fontsHeight = labelFontSize + inputFontSize + errorFontSize + 10;
    const internalVerticalSpaces = 16;
    return fontsHeight + internalVerticalSpaces;
  };

  const getErrorContentSpace = () => {
    return errorFontSize + 2;
  };

  const animationView = useCallback(() => {
    const inputAdjust = {
      borderColor: animatedIsFocused.interpolate({
        inputRange: [0, 1],
        outputRange: [C('Label/Light/Primary'), C('Hairline/Light/Primary')],
      }),
    };
    return inputAdjust;
  }, [animatedIsFocused, C.schemeName]);

  const generateActionText = () => {
    const isTextExist = error.indexOf(errorAction.text);
    if (isTextExist === -1) {
      return error;
    }
    const textArray = error.split(` ${errorAction.text} `);
    return (
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center' }}>
        <Text style={[font['Caption 2'], { color: errorColor, flexDirection: 'row', flexWrap: 'wrap' }]}>
          {i18n.t(textArray[0])}
          <TouchableWithoutFeedback onPress={errorAction.action}>
            <Text style={[font['Button 3'], { color: errorColor, cursor: 'pointer' }]}> {i18n.t(errorAction.text)} </Text>
          </TouchableWithoutFeedback>
          {i18n.t(textArray[1])}
        </Text>
      </View>
    );
  };

  return [
    <View style={[styles.content, styleContent, { height: setContentHeight() }]}>
      <Animated.View
        style={[
          styles.bodyContent,
          styleBodyContent,
          {
            marginBottom: showError ? 0 : getErrorContentSpace(),
          },
          animationView(),
        ]}
      >
        <View
          style={{
            flex: 1,
            justifyContent: 'space-between',
          }}
        >
          <Animated.Text style={[styles.label, styleLabel, animationLabelFontSize()]}>
            <Text
              style={{
                backgroundColor: C('Background / Light / System'),
              }}
            >
              &nbsp;&nbsp;{placeholder}&nbsp;&nbsp;
            </Text>
          </Animated.Text>
          <TextInput
            {...others}
            value={value}
            autoCapitalize="none"
            autoCompleteType={autoCompleteType}
            pointerEvents={disabled ? 'box-none' : 'auto'}
            selectionColor={styleInput.fontColor}
            blurOnSubmit
            editable={!disabled}
            onBlur={() => onBlur()}
            style={[styles.input, styleInput, { color: C('Label/Light/Primary'), outline: 'none', zIndex: 110 }]}
            secureTextEntry={secure}
            onEndEditing={() => {
              onBlur();
            }}
            onFocus={() => onFocus()}
          />
        </View>
      </Animated.View>
    </View>,
    error ? (
      <View
        style={{
          paddingHorizontal: scale(4),
          flexDirection: 'row',
          width: '100%',
          justifyContent: 'center',
          marginTop: scale(8),
        }}
        key={`InputBase-error-${placeholder}`}
      >
        <View style={{ flexDirection: 'row', width: '100%' }}>
          <Svg icon="exclamationCircle" width={scale(11)} height={scale(20)} color="Label/Light/Error" />
          <View style={{ paddingRight: scale(16), width: '100%', paddingLeft: scale(8), justifyContent: 'center' }}>
            {errorAction ? generateActionText() : <Text style={[font['Button 3'], { color: errorColor }]}>{i18n.t(error)}</Text>}
          </View>
        </View>
      </View>
    ) : (
      <View key={`InputBase-no-error-${placeholder}`} />
    ),
  ];
};

AnimatedInput.defaultProps = {
  valid: true,
  disabled: false,
  secure: false,
  value: '',
  styleInput: {},
  styleBodyContent: {},
  styleLabel: {},
  styleError: {},
};

export default ColorWrapper(AnimatedInput);

const styles = StyleSheet.create({
  content: {
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: 1,
  },
  input: {
    fontSize: 15,
    width: '100%',
    paddingLeft: 16,
    paddingRight: 16,
    height: 54,
    zIndex: 10,
    borderRadius: 6,
  },
  bodyContent: {
    borderWidth: 0.5,
    borderRadius: 6,
    justifyContent: 'flex-start',
    flexDirection: 'row',
    height: scale(54),
    width: '100%',
  },
  toucheableLineContent: {
    flexDirection: 'row',
    alignItems: 'baseline',
    position: 'absolute',
    zIndex: 10,
    top: 18,
    width: '100%',
  },
  label: {
    fontSize: 12,
    fontWeight: 'normal',
    alignSelf: 'flex-start',
    paddingLeft: 16,
    paddingRight: 5,
    marginLeft: 6,
    left: -16,
    position: 'absolute',
    borderRadius: 10,
    width: !isPhone ? 382 : 302,
    height: scale(54),
    zIndex: 100,
  },
  error: {
    marginBottom: 5,
    color: '#d32f2f', // ColorPoint
    fontSize: 13,
    marginTop: 2,
  },
  sufix: {
    flexDirection: 'column-reverse',
  },
});
